<template>
  <div class="od-page">
    <div class="od-body">
      <header class="od-top">
        <button type="button" class="od-back" aria-label="返回" @click="goBack">
          <img src="@/assets/images/svg/back.svg" alt="" />
        </button>
        <h1 class="od-title">申请售后</h1>
      </header>

      <section class="od-card">
        <h2 class="od-card__h">商品信息</h2>
        <div class="after-goods-wrap">
          <goodsInfo
            v-for="(x, index) in goodsList"
            :key="index"
            :goodsData="x"
          />
        </div>
      </section>

      <section class="od-card">
        <h2 class="od-card__h">售后方式</h2>
        <el-radio-group v-model="way" class="after-way-group" size="large">
          <el-radio-button label="RETURN_REFUND">退货退款</el-radio-button>
          <el-radio-button label="REFUND">仅退款</el-radio-button>
          <el-radio-button label="REPLACE_REFUND">换货</el-radio-button>
        </el-radio-group>
      </section>

      <section class="od-card">
        <h2 class="od-card__h">退款原因</h2>
        <p class="od-hint">请选择真实原因，方便商家处理</p>
        <div class="after-reason-grid">
          <label
            v-for="item in radioData"
            :key="item.id"
            class="after-reason-item"
          >
            <el-radio
              v-model="radio"
              :label="item.name"
              class="after-reason-radio"
            >
              {{ item.name }}
            </el-radio>
          </label>
        </div>
      </section>

      <section class="od-card after-card--amount">
        <h2 class="od-card__h">申请退款金额</h2>
        <div class="after-amount">
          <span class="after-amount__value">¥{{ formattedRefund }}</span>
          <span class="after-amount__note">不可修改，与当前可退金额一致</span>
        </div>
      </section>

      <section class="od-card">
        <div class="od-card__row">
          <h2 class="od-card__h od-card__h--inline">补充描述</h2>
          <span class="od-optional">选填</span>
        </div>
        <el-input
          v-model="applyReason"
          class="after-textarea"
          type="textarea"
          :rows="4"
          maxlength="500"
          show-word-limit
          placeholder="可补充说明情况，便于更快处理"
        />
      </section>

      <div class="od-actions">
        <el-button
          type="primary"
          class="od-btn od-btn--primary"
          @click="goAfterSale"
        >
          提交申请
        </el-button>
        <el-button class="od-btn od-btn--ghost" @click="goBack">返回</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ElMessage } from "element-plus";
import { applyRefund } from "@/api/shopping";
import goodsInfo from "@/components/goodsInfo.vue";

const router = useRouter();
const radio = ref("多拍/错拍/不想要");
const way = ref("RETURN_REFUND");
const applyReason = ref("");
const orderData = ref({});
const goodsList = ref([]);
const radioData = ref([
  { name: "多拍/错拍/不想要", id: "1" },
  { name: "商家发错货", id: "2" },
  { name: "填错地址/不方便收货", id: "3" },
  { name: "商品买贵了或降价", id: "4" },
  { name: "快递一直未送达", id: "5" },
  { name: "少件/漏发", id: "6" },
  { name: "未按约定时间发货", id: "7" },
  { name: "其他", id: "8" },
]);

const formattedRefund = computed(() => {
  const n = Number(orderData.value?.refundAmount);
  if (Number.isNaN(n)) return "0.00";
  return n.toFixed(2);
});

onMounted(() => {
  const route = useRoute();
  try {
    const data = JSON.parse(route.query.data || "{}");
    fetchData(data);
  } catch {
    ElMessage.error("订单数据无效，请从订单详情重新进入");
    router.push("/myOrder");
  }
});

const fetchData = (data) => {
  orderData.value = data;
  goodsList.value = data.items || [];
};

const goBack = () => {
  router.go(-1);
};

const goAfterSale = () => {
  const id = orderData.value?.id;
  if (!id) {
    ElMessage.warning("订单信息异常");
    return;
  }
  const wayText =
    way.value === "RETURN_REFUND"
      ? "退货退款"
      : way.value === "REFUND"
      ? "仅退款"
      : "换货";
  const refundReason = [wayText, "原因: " + radio.value]
    .concat(applyReason.value ? ["补充: " + applyReason.value] : [])
    .join("；");
  applyRefund({ id, refundReason })
    .then((res) => {
      if (res.code === 200) {
        ElMessage.success("售后申请已提交，请等待商家处理");
        router.push(`/myOrder/detail/${id}`);
      } else {
        ElMessage.error(res.message || "提交失败");
      }
    })
    .catch((err) => {
      ElMessage.error(
        err?.response?.data?.message || err?.message || "提交失败"
      );
    });
};
</script>

<style scoped lang="scss">
@import "@/styles/order-flow.scss";

.od-actions .od-btn {
  min-width: 160px;
  height: 48px !important;
  font-size: 16px !important;
}

.after-goods-wrap {
  display: flex;
  flex-direction: column;
  gap: 14px;

  :deep(.info_list) {
    width: 100% !important;
    max-width: 100%;
    margin: 0;
    padding: 16px 18px;
    background: var(--ux-panel-bg, rgba(255, 255, 255, 0.04));
    border: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.08));
    border-radius: var(--ios-radius-md, 14px);
  }

  :deep(.left_title) {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  :deep(.pic) {
    background: var(--ux-surface-muted, rgba(255, 255, 255, 0.06)) !important;
    border-radius: 12px;
  }

  :deep(.text_title),
  :deep(.text_count),
  :deep(.price) {
    color: var(--ux-text-primary, #e6f0ff);
  }

  :deep(.mid_title),
  :deep(.mid_item),
  :deep(.goodsData) {
    color: var(--ux-text-secondary, #9fb4d6);
  }
}

.after-way-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;

  :deep(.el-radio-button__inner) {
    border-radius: var(--ios-radius-md, 14px) !important;
    border: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.1)) !important;
    background: var(--ux-surface-muted, rgba(255, 255, 255, 0.08));
    color: var(--ux-text-primary, #e6f0ff);
    font-size: 15px;
    padding: 10px 20px;
    box-shadow: none !important;
  }

  :deep(.el-radio-button:first-child .el-radio-button__inner) {
    border-left: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.1)) !important;
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    color: #fff !important;
    background: linear-gradient(
      135deg,
      var(--ux-accent, #00d4ff) 0%,
      var(--ux-accent-2, #a78bfa) 100%
    ) !important;
    border-color: transparent !important;
    box-shadow: 0 0 14px var(--ux-accent-soft, rgba(0, 212, 255, 0.25)) !important;
  }
}

.after-reason-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 20px;
}

.after-reason-item {
  display: block;
  padding: 10px 12px;
  border-radius: var(--ios-radius-sm, 10px);
  background: var(--ux-panel-bg, rgba(255, 255, 255, 0.04));
  border: 0.5px solid transparent;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.after-reason-item:hover {
  border-color: rgba(0, 212, 255, 0.2);
}

.after-reason-radio {
  width: 100%;
  height: auto;
  margin: 0;
  white-space: normal;
  align-items: flex-start;

  :deep(.el-radio__label) {
    font-size: 15px;
    line-height: 1.4;
    color: var(--ux-text-primary, #e6f0ff);
    padding-left: 8px;
  }
}

.after-card--amount .after-amount {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px 20px;
}

.after-amount__value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--ux-text-primary, #e6f0ff);
}

.after-amount__note {
  font-size: 14px;
  color: var(--ux-text-muted, rgba(159, 180, 214, 0.75));
}

.after-textarea {
  width: 100%;

  :deep(.el-textarea__inner) {
    border-radius: var(--ios-radius-md, 14px);
    background: var(--ux-surface-muted, rgba(255, 255, 255, 0.06));
    border: 0.5px solid var(--ios-separator, rgba(255, 255, 255, 0.1));
    color: var(--ux-text-primary, #e6f0ff);
    font-size: 15px;
    line-height: 1.5;
    min-height: 120px;
    box-shadow: none;
  }

  :deep(.el-textarea__inner:focus) {
    border-color: rgba(0, 212, 255, 0.35);
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.1);
  }

  :deep(.el-input__count) {
    color: var(--ux-text-muted, rgba(159, 180, 214, 0.55));
    background: transparent;
  }
}

@media (max-width: 768px) {
  .after-reason-grid {
    grid-template-columns: 1fr;
  }

  .after-way-group {
    flex-direction: column;

    :deep(.el-radio-button) {
      width: 100%;
    }

    :deep(.el-radio-button__inner) {
      width: 100%;
      text-align: center;
    }
  }
}
</style>
